import React, { useContext, useEffect, useRef, useState } from "react";
import {
  SearchbarChangeEventDetail,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPage,
  IonSearchbar,
  IonTitle,
  IonToolbar,
  IonLoading,
  useIonAlert
} from "@ionic/react";
import { TranslationsContext, TranslationMessagesContext } from "../util/Translations";
import { findStoreItem } from "../api/ItemLocations";
import { SearchResult } from "../models/materialissue/MaterialIssue";
import { useQueryExec } from "../hooks/useQueryExec";
import QueryFilter from "./QueryFilter";
import InventoryResults from "./InventoryResults";
import SearchFooter from "./SearchFooter";
import { chevronBackOutline, barcodeOutline, barcodeSharp } from "ionicons/icons";
import { ColumnsWithGroupData, getColumnsWithGroup } from "../api/Windows";
import { GetColumnsWithGroupResponse } from "../models/windows/GetColumnsWithGroup";
import { IonSearchbarCustomEvent } from "@ionic/core";
import {
  performBarCodeShimProcess,
  findObjectByElementType,
} from "../util/BarCodeHelper";
import { useBarcodeManager } from "../hooks/useBarcodeManager";
import BarcodeScannerButton from "./BarcodeScannerButton";

interface MaterialIssueItemSearchProps {
  isOpen: boolean;
  onSelect: (index: string) => void;
  onCancel: () => void;
  storeLocation?: string;
  issueOrReturn: string;
}

const MaterialIssueItemSearch: React.FC<MaterialIssueItemSearchProps> = (
  props
) => {
  const { onSelect, onCancel, storeLocation, issueOrReturn } = props;
  const { translations } = useContext(TranslationsContext);
  const [metadata, setMetadata] = useState<GetColumnsWithGroupResponse>();
  const { scanText, setScanText, scanClick } = useBarcodeManager();
  const [advSearchOpen, setAdvSearchOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [presentAlert] = useIonAlert();
  const {
    getQueryList,
    queryList,
    getFilterParams,
    filterParams,
    setParameter,
    showLoading,
    setShowLoading,
    queryRows,
    executeSearch,
    errorState,
    totalCount,
    queryCriteria,
    getQueryCriteria,
    getNoOfFieldsToDisplay,
    searchYn,
    setSearchYn,
    queryName,
    criteriaParams,
    confirmSearch,
    execFilter,
    resultsFilter,
    filterRows,
    queryInfo
  } = useQueryExec("Item", "Item", "item");
  const [itemVal, setItemVal] = useState<string>("");
  const querySection = "QW--Item";
  const metadataQueryCalled = useRef(false); // Ref to track if metadataQuery has been called
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [hasSearched, setHasSearched] = useState(false);
  const searchbarRef = useRef<HTMLIonSearchbarElement>(null);

  useEffect(() => {
    getIssueAddPartsColumns();
  }, []);

  const getIssueAddPartsColumns = async () => {
    if (metadataQueryCalled.current) return; // If already called, return early
    metadataQueryCalled.current = true;
    const data: ColumnsWithGroupData = {
      WindowName:
        issueOrReturn === "issue"
          ? "mob_inventory_issue_addparts"
          : "mob_inventory_return_addparts",
      IncludeValidValues: "true",
    };
    const response = await getColumnsWithGroup(data);
    setMetadata(response.data);
  };

  const search = async (searchString: string, storeLocation: string) => {
    const params = {
      searchTerm: searchString,
      store: storeLocation,
    };
    const response = await findStoreItem(params);
    const searchResults = parseSearchResponse(response.data);
    setResults(searchResults);
  };

  const getSearchParameters = async (searchString: string) => {
    setShowLoading(true);
    try {
      if (storeLocation === "") {
        presentAlert("Please select a store location to search items.");
        setResults([]);
        setHasSearched(false);
      } else if (storeLocation && searchString && searchString.length > 2) {
        await search(searchString, storeLocation);
        setHasSearched(true);
      } else {
        setResults([]);
        setHasSearched(false);
      }
    } finally {
      setShowLoading(false);
      setTimeout(() => {
        searchbarRef.current?.setFocus();
      }, 0);
    }
  };

  const parseSearchResponse = (response: any) => {
    const results: SearchResult[] = [];
    const keys = Object.keys(response);
    const values = Object.values(response);
    for (let i = 0; i < keys.length; i++) {
      results.push({ item: keys[i], description: String(values[i] || "") });
    }
    return results;
  };

  const handleCancel = () => {
    setScanText("");
    onCancel();
  };

  const handleItemClick = (item: string) => {
    onSelect(item);
    setScanText("");
    handleClose();
  };

  const handleOpen = () => {
    setAdvSearchOpen(true);
  };
  const handleClose = () => {
    setAdvSearchOpen(false);
  };

  async function onScanTextEnter(
    ev: IonSearchbarCustomEvent<SearchbarChangeEventDetail>
  ): Promise<boolean> {
    setShowLoading(true);
    try {
      let res = String(ev.target.value);
      setItemVal(res);
      if (itemVal === res) {
        setShowLoading(false);
        return false;
      }
      const barCodeFieldData = findObjectByElementType(metadata?.fields);
      const barcodeResult = await performBarCodeShimProcess(
        barCodeFieldData,
        res
      );
      if (!barcodeResult) {
        setShowLoading(false);
        return false;
      }
      if (barcodeResult.NoMatch) {
        setItemVal(res);
        setScanText(res);
        setShowLoading(false);
        return false;
      }
      let capturingGroups = barcodeResult.CapturingGroupValues;
      if (!(capturingGroups?.Item || barcodeResult.FirstCapturingGroup)) {
        setShowLoading(false);
        return false;
      }
      res = capturingGroups?.Item || barcodeResult.FirstCapturingGroup;
      setItemVal(res);
      setScanText(res);
      // Automatically add the item when barcode is successfully scanned
      handleItemClick(res);
      setResults([]);
      setHasSearched(false);
      return true;
    } finally {
      setShowLoading(false);
    }
  }

  const ResultsModal = (
    <IonModal isOpen={advSearchOpen} onIonModalWillDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={handleClose}>
              {translations["lbl_btn_cancel"] || "Cancel"}
            </IonButton>
          </IonButtons>
          <IonTitle>
            {translations["lbl_item_search"] || "Item Search"}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <QueryFilter
          setShowLoading={setShowLoading}
          getQueryList={getQueryList}
          queryList={queryList}
          querySection={querySection}
          getFilterParams={getFilterParams}
          filterParams={filterParams}
          setParameter={setParameter}
          queryCriteria={queryCriteria}
          getQueryCriteria={getQueryCriteria}
          searchCallback={confirmSearch}
          queryInfo={queryInfo}
          triggerId="item-search-popover-trigger"
        />
        <InventoryResults
          searchYn={searchYn}
          setSearchYn={setSearchYn}
          queryName={queryName}
          queryParams={filterParams}
          criteria={criteriaParams}
          queryRows={queryRows}
          filterParams={filterParams}
          setParameter={setParameter}
          executeSearch={executeSearch}
          getNoOfFieldsToDisplay={getNoOfFieldsToDisplay}
          errorState={errorState}
          totalCount={totalCount}
          showLoading={showLoading}
          setShowLoading={setShowLoading}
          filterRows={filterRows}
          filterMethod={execFilter}
          filterString={resultsFilter}
          cardClick={handleItemClick}
        />
      </IonContent>
      <SearchFooter totalRows={totalCount} filterRows={filterRows} />
    </IonModal>
  );
  return (
    <>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
      />
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={handleCancel}>
                <IonIcon icon={chevronBackOutline} />
              </IonButton>
            </IonButtons>
            <IonTitle>{translations["lbl_add_item"] || "Add Item"}</IonTitle>
          </IonToolbar>
          <IonToolbar>
            <IonSearchbar
              ref={searchbarRef}
              value={scanText}
              onIonChange={async (ev) => {
                const value = ev.detail.value;
                if (!value) {
                  setResults([]);
                  setHasSearched(false);
                  return;
                }  
                setScanText(value);
                const barcodeSuccess = await onScanTextEnter(ev);
                if (!barcodeSuccess) {
                  getSearchParameters(value);
                }
              }}
              debounce={1500}
              placeholder={
                metadata?.fields["in_item_location.item"]?.TranslatedIdText
              }
            ></IonSearchbar>
            <IonButton color="primary" expand="full" onClick={() => handleOpen()}>
              {translations["lbl_btn_mob_advancedsearch"] || "Advanced Search"}
            </IonButton>
            <IonButton 
              color="primary" 
              expand="block" 
              fill="outline"
              onClick={() => scanClick()}
              style={{ width: '100%', flex: 1 }}
            >
              <IonIcon
                slot="start"
                ios={barcodeOutline}
                md={barcodeSharp}
              />
              {translations["lbl_mob_scanbarcode"] || "Scan Barcode"}
            </IonButton>
          </IonToolbar>
        </IonHeader>
        {ResultsModal}
        <IonContent class="ion-padding">
          {results.length > 0 ? (
            <IonList lines="full">
              {results.map((result, index) => (
                <IonItem
                  button
                  key={index}
                  onClick={() => {
                    handleItemClick(result.item);
                  }}
                >
                  <IonLabel>{`${result.item} - ${result.description}`}</IonLabel>
                </IonItem>
              ))}
            </IonList>
          ) : hasSearched && !showLoading ? (
            <IonLabel
              color={"warning"}
              style={{ padding: "16px", display: "inline-block" }}
            >
              {translatedMessages["NoResultsFound"]?.MessageText ||
                "No results found."}
            </IonLabel>
          ) : null}
        </IonContent>
      </IonPage>
    </>
  );
};

export default MaterialIssueItemSearch;
